<template>
  <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
    <div
      class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25"
    >
      <div class="d-sm-flex align-items-center">
        <h5 class="mb-0">Liste des devoirs soumis</h5>
      </div>
      <div class="d-flex align-items-center">
        <form
          class="search-box position-relative me-15"
          @submit.prevent="rechercher"
        >
          <input
            type="text"
            v-model="searchTerm"
            @keyup="rechercher"
            class="form-control shadow-none text-black rounded-0 border-0"
            placeholder="Rechercher un devoir..."
          />
          <button
            type="submit"
            class="bg-transparent text-primary transition p-0 border-0"
          >
            <i class="flaticon-search-interface-symbol"></i>
          </button>
        </form>
      </div>
    </div>

    <div class="card-body p-15 p-sm-20 p-md-25">
      <!-- Message si aucun devoir -->
      <div v-if="devoirs.length === 0 && !isLoading" class="alert alert-info">
        Aucun devoir soumis.
      </div>

      <!-- Loader -->
      <div v-if="isLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Chargement...</span>
        </div>
      </div>

      <!-- Tableau des devoirs -->
      <div v-else-if="devoirs.length > 0" class="table-responsive">
        <table class="table text-nowrap align-middle mb-0">
          <thead>
            <tr>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Étudiant
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Composition
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Fichier
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Soumission
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Statut
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Note
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text pe-0">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="devoir in devoirs" :key="devoir.id">
              <!-- Étudiant -->
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                {{ devoir.etudiant?.prenom }} {{ devoir.etudiant?.nom }}
              </td>

              <!-- Composition -->
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                {{ devoir.composition?.titre || '—' }}
              </td>

              <!-- Nom du fichier -->
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                <small>{{ truncate(devoir.fichierNom, 25) }}</small>
              </td>

              <!-- Date de soumission  -->
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                <small>{{ formatDate(devoir.dateSoumission) }}</small>
              </td>

              <!-- Statut -->
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                <span
                  :class="getStatutBadge(devoir.statut)"
                  class="badge"
                >
                  {{ formatStatut(devoir.statut) }}
                </span>
              </td>

              <!-- Note -->
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                <strong v-if="devoir.note !== null && devoir.note !== undefined">
                  {{ devoir.note }}/20
                </strong>
                <span v-else class="text-muted">—</span>
              </td>

              <!-- Actions -->
              <td class="shadow-none lh-1 fw-medium text-body-tertiary text pe-0">
                <div class="dropdown">
                  <span
                    class="badge text-white bg-primary fs-15 dropdown-toggle"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    style="cursor: pointer;"
                  >
                    Actions
                    <i class="flaticon-chevron-2 position-relative ms-5 top-2 fs-15"></i>
                  </span>
                  <ul class="dropdown-menu">
                    <li v-if="devoir.statut !== 'corrige' && devoir.composition?.type !== 'QCM'">
                    <router-link
                        :to="{ name: 'ViewDevoirPage', params: { id: devoir.id } }"
                        class="dropdown-item d-flex align-items-center text-success"
                    >
                        <i class="flaticon-edit lh-1 me-8 position-relative top-1"></i>
                        Corriger
                    </router-link>
                    </li>
                    <li>
                      <!-- <a
                        class="dropdown-item d-flex align-items-center"
                        href="javascript:void(0);"
                        @click="downloadDevoir(devoir.id)"
                      >
                        <i class="flaticon-download lh-1 me-8 position-relative top-1"></i>
                        Télécharger
                      </a> -->
                    </li>
                    
                    <li>
                      <a
                        class="dropdown-item d-flex align-items-center text-danger"
                        href="javascript:void(0);"
                        @click="confirmDelete(devoir.id, devoir.fichierNom)"
                      >
                        <i class="flaticon-delete lh-1 me-8 position-relative top-1"></i>
                        Supprimer
                      </a>
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="devoirs.length > 0" class="pagination-area d-md-flex mt-15 mt-sm-20 mt-md-25 justify-content-between align-items-center">
        <PaginationComponent
          :page="page"
          :totalPages="totalPages"
          :totalElements="totalElements"
          :limit="limit"
          @paginate="handlePaginate"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import Swal from "sweetalert2";
import ApiService from "../../services/ApiService";
import { error, success } from "../../utils/utils";
import PaginationComponent from "../../components/Utilities/Pagination.vue";

interface Devoir {
  id: number;
  fichierNom: string;
  dateSoumission: string; 
  statut: "soumis" | "en_cours" | "corrige";
  note: number | null;
  etudiant: {
    nom: string;
    prenom: string;
  };
  composition: {
    titre: string;
    type: string; 
    typeComposition?: 'QCM' | 'QuestionsReponses';
    filiereNiveauMatiere?: {
      filiere?: { libelle: string };
      niveau?: { libelle: string };
      matiere?: { libelle: string };
    };
  };
}

export default defineComponent({
  name: "ListeDevoirs",
  components: { PaginationComponent },
  setup() {
    const devoirs = ref<Devoir[]>([]);
    const searchTerm = ref("");
    const page = ref(1);
    const totalPages = ref(0);
    const limit = ref(10);
    const totalElements = ref(0);
    const isLoading = ref(false);

 const getAllDevoirs = (page_ = page.value, limit_ = limit.value) => {
  isLoading.value = true;

  const params = new URLSearchParams({
    page: page_.toString(),
    limit: limit_.toString(),
  });
  
  if (searchTerm.value && searchTerm.value.trim()) {
    params.append('searchTerm', searchTerm.value.trim());
  }

  return ApiService.get(`/devoirs?${params.toString()}`) // ENLEVEZ le "/" à la fin
    .then(({ data }) => {
      if (data.code === 200 && data.data) {
        devoirs.value = data.data.data || [];
        totalPages.value = data.data.totalPages || 0;
        limit.value = data.data.limit || 10;
        totalElements.value = data.data.totalElements || 0;
        page.value = page_;
      } else {
        devoirs.value = [];
      }
    })
    .catch((err) => {
      console.error('Erreur getAllDevoirs:', err);
      error(err.response?.data?.message || "Erreur lors de la récupération des devoirs");
      devoirs.value = [];
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const handlePaginate = ({ page_, limit_ }: { page_: number; limit_: number }) => {
  page.value = page_;
  limit.value = limit_;
  getAllDevoirs(page_, limit_);
};

const rechercher = () => {
  page.value = 1;
  getAllDevoirs();
};


    const downloadDevoir = async (id: number) => {
  try {
    // Ajout du paramètre 'type=original' dans l'URL
    const response = await ApiService.get(`/devoirs/${id}/download?type=original`, {
      responseType: 'blob',
      timeout: 30000
    });
   
    // Vérifier que la réponse est bien un blob
    if (!(response.data instanceof Blob)) {
      throw new Error('La réponse n\'est pas un fichier valide');
    }

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
   

    const devoir = devoirs.value.find(d => d.id === id);
    const fileName = devoir?.fichierNom || `devoir_${id}.pdf`;
    link.setAttribute('download', fileName);
   
    document.body.appendChild(link);
    link.click();
   
    // Nettoyage
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
   
    success("Téléchargement réussi");
  } catch (err: any) {
    console.error('Erreur téléchargement:', err);
   
    // Gestion d'erreur améliorée
    if (err.response?.status === 404) {
      error("Le fichier demandé n'existe pas ou a été supprimé");
    } else if (err.response?.status === 403) {
      error("Vous n'avez pas les permissions requises pour télécharger ce fichier");
    } else {
      error(err.response?.data?.message || "Erreur lors du téléchargement");
    }
  }
};


    const deleteDevoir = async (id: number) => {
  try {
    const { data } = await ApiService.delete(`/devoirs/${id}`);
    success(data.message || "Devoir supprimé avec succès");
    // Utiliser getAllDevoirs sans le 3ème paramètre
    await getAllDevoirs(page.value, limit.value);
  } catch (err: any) {
    error(err.response?.data?.message || "Impossible de supprimer le devoir");
  }
};

    const confirmDelete = (id: number, fichierNom: string) => {
      Swal.fire({
        title: "Supprimer ce devoir ?",
        text: `Fichier : ${fichierNom}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Oui, supprimer",
        cancelButtonText: "Annuler",
        customClass: {
          confirmButton: "btn fw-semibold btn-danger me-2",
          cancelButton: "btn fw-semibold btn-light",
        },
      }).then((result) => {
        if (result.isConfirmed) deleteDevoir(id);
      });
    };

    const formatDate = (date: string | Date): string => {
      if (!date) return '—';
      try {
        return new Date(date).toLocaleString('fr-FR', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }).replace(',', '');
      } catch (e) {
        return 'Date invalide';
      }
    };

    const formatStatut = (statut: string): string => {
      switch (statut) {
        case 'soumis': return 'Soumis';
        case 'en_cours': return 'En cours';
        case 'corrige': return 'Corrigé';
        default: return statut || 'N/A';
      }
    };

    const getStatutBadge = (statut: string): string => {
      switch (statut) {
        case 'soumis': return 'bg-warning';
        case 'en_cours': return 'bg-info';
        case 'corrige': return 'bg-success';
        default: return 'bg-secondary';
      }
    };

    const truncate = (text: string | undefined, length: number): string => {
      if (!text) return '—';
      return text.length > length ? text.substring(0, length) + '...' : text;
    };

    onMounted(() => {
      getAllDevoirs();
    });

    return {
      devoirs,
      page,
      totalPages,
      limit,
      totalElements,
      searchTerm,
      isLoading,
      handlePaginate,
      rechercher,
      downloadDevoir,
      confirmDelete,
      formatDate,
      formatStatut,
      getStatutBadge,
      truncate,
    };
  },
});
</script>

<style scoped>
.badge {
  font-size: 0.75rem;
  padding: 0.35em 0.65em;
}
</style>